"use client";

import React from "react";
import { Upload, Check, Loader, FileText, ShieldCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const RequestForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="rounded-full bg-green-500/10 p-6 ring-1 ring-green-500/20">
          <Check className="size-12 text-green-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-semibold text-primary">
            Richiesta Inviata
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 hover:bg-primary/10 hover:text-primary">
          Invia una nuova richiesta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/80">
                Nome e Cognome <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" {...field} className="bg-[#F8F8FF] text-slate-900 border-slate-200 placeholder:text-slate-400 focus-visible:ring-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserisci un indirizzo email valido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/80">
                Indirizzo Email <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="mario.rossi@example.com" {...field} className="bg-[#F8F8FF] text-slate-900 border-slate-200 placeholder:text-slate-400 focus-visible:ring-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Il telefono è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/80">
                Telefono <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 333 1234567" {...field} className="bg-[#F8F8FF] text-slate-900 border-slate-200 placeholder:text-slate-400 focus-visible:ring-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/80">
                Azienda / Organizzazione
              </FormLabel>
              <FormControl>
                <Input placeholder="Nome della tua azienda" {...field} className="bg-[#F8F8FF] text-slate-900 border-slate-200 placeholder:text-slate-400 focus-visible:ring-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="request_type" rules={{
      required: "Seleziona un tipo di richiesta"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground/80">
              Tipo di Richiesta <span className="text-primary">*</span>
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-[#F8F8FF] text-slate-900 border-slate-200 focus:ring-primary/50">
                  <SelectValue placeholder="Seleziona il tipo di servizio" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-[#F8F8FF] text-slate-900 border-slate-200">
                {["Redazione Contratto", "Revisione Contratto", "Consulenza Legale", "Negoziazione", "Altro"].map(option => <SelectItem key={option} value={option} className="focus:bg-slate-200 focus:text-slate-900">
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" rules={{
      required: "La descrizione è obbligatoria"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground/80">
              Descrizione della Richiesta <span className="text-primary">*</span>
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Fornisci una breve descrizione del tuo caso o della tua necessità..." className="min-h-[120px] bg-[#F8F8FF] text-slate-900 border-slate-200 placeholder:text-slate-400 focus-visible:ring-primary/50 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem>
            <FormLabel className="text-primary-foreground/80">
              Allega Documenti (opzionale)
            </FormLabel>
            <FormControl>
              <div className="relative group">
                <Input {...fieldProps} type="file" className="bg-[#F8F8FF] text-slate-900 border-slate-200 file:bg-slate-200 file:text-slate-700 file:border-0 file:mr-4 file:py-1 file:px-3 file:rounded-sm hover:file:bg-slate-300 transition-all cursor-pointer" onChange={event => {
            onChange(event.target.files && event.target.files[0]);
          }} />
                <Upload className="absolute right-3 top-2.5 size-4 text-slate-400 pointer-events-none" />
              </div>
            </FormControl>
            <FormDescription className="text-primary-foreground/50 text-xs">
              Puoi caricare contratti, bozze o altri documenti rilevanti (max
              5MB).
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="privacy_agreement" rules={{
      required: "Devi accettare la privacy policy per procedere"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-primary/20 p-4 bg-primary/5">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-primary/50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-primary-foreground/90 font-normal">
                Ho letto e accetto la{" "}
                <Link to="/privacy-policy" className="text-primary hover:underline font-medium">
                  Privacy Policy
                </Link>{" "}
                e acconsento al trattamento dei miei dati personali.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-lg animate-in fade-in slide-in-from-top-2">
          <p className="text-red-200 text-sm flex items-center gap-2">
            <span className="text-red-500 font-bold">Errore:</span>{" "}
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-lg h-12 shadow-lg shadow-primary/10 transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Invio in corso...
          </> : "Invia Richiesta"}
      </Button>
    </Form>;
};
export default function InviaUnaRichiesta() {
  return;
}